import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LogoMark } from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export function PaymentSuccess() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, paid: false, error: "Missing session" }); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, error: null });
          refresh();
          return;
        }
        if (data.status === "expired" || attempts.current >= 8) {
          setState({ loading: false, paid: false, error: t.payment.failed });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ loading: false, paid: false, error: t.payment.failed });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return <PayResult state={state} />;
}

export function PaymentCancel() {
  const { t } = useLang();
  return <PayResult state={{ loading: false, paid: false, error: t.payment.cancelled }} />;
}

function PayResult({ state }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2] p-6" data-testid="payment-result">
      <div className="aq-card p-12 max-w-lg w-full text-center">
        <div className="flex justify-center mb-8"><LogoMark size={56} /></div>
        {state.loading && (
          <>
            <Loader2 className="animate-spin mx-auto text-[#1E3A5F]" size={36} />
            <p className="mt-6 font-mono-aq text-sm tracking-wider">{t.payment.checking}</p>
          </>
        )}
        {!state.loading && state.paid && (
          <>
            <CheckCircle2 className="mx-auto text-[#1E3A5F]" size={42} />
            <h1 className="font-serif-display text-3xl md:text-4xl mt-5 tracking-tight">{t.payment.success}</h1>
          </>
        )}
        {!state.loading && !state.paid && (
          <>
            <XCircle className="mx-auto text-[#0A0A0A]" size={42} />
            <h1 className="font-serif-display text-3xl md:text-4xl mt-5 tracking-tight">{state.error || t.payment.failed}</h1>
          </>
        )}
        {!state.loading && (
          <Link to="/dashboard" className="aq-button-primary mt-10 inline-flex" data-testid="payment-back">
            {t.payment.back} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  );
}
